import { FREE_PLAN_LIMITS, tenantCanViewAnalytics, tenantCanUseAiAssistant } from '@brix/types'
import type { Plan, CustomPlanLimits } from '@brix/types'

export interface TenantProps {
  id: string
  name: string
  slug: string
  plan: Plan
  customLimits: CustomPlanLimits | null
  createdAt: Date
  updatedAt: Date
}

const SITE_LIMITS: Record<Exclude<Plan, 'custom'>, number> = {
  free: 1,
  starter: 5,
  pro: Infinity,
  enterprise: Infinity,
}

const PAGE_LIMITS: Record<Exclude<Plan, 'custom'>, number> = {
  free: 5,
  starter: Infinity,
  pro: Infinity,
  enterprise: Infinity,
}

const POST_LIMITS: Record<Exclude<Plan, 'custom'>, number> = {
  free: 5,
  starter: Infinity,
  pro: Infinity,
  enterprise: Infinity,
}

export class Tenant {
  readonly id: string
  readonly name: string
  readonly slug: string
  readonly plan: Plan
  readonly customLimits: CustomPlanLimits | null
  readonly createdAt: Date
  readonly updatedAt: Date

  private constructor(props: TenantProps) {
    this.id = props.id
    this.name = props.name
    this.slug = props.slug
    this.plan = props.plan
    this.customLimits = props.customLimits
    this.createdAt = props.createdAt
    this.updatedAt = props.updatedAt
  }

  static reconstitute(props: TenantProps): Tenant {
    return new Tenant(props)
  }

  private resolvedCustomLimits(): CustomPlanLimits {
    return this.customLimits ?? FREE_PLAN_LIMITS
  }

  canCreateSite(currentCount: number): boolean {
    const limit = this.plan === 'custom' ? this.resolvedCustomLimits().siteLimit : SITE_LIMITS[this.plan]
    return currentCount < limit
  }

  canCreatePage(currentCount: number): boolean {
    const limit = this.plan === 'custom' ? this.resolvedCustomLimits().pageLimit : PAGE_LIMITS[this.plan]
    return currentCount < limit
  }

  canCreateBlogPost(currentCount: number): boolean {
    const limit = this.plan === 'custom' ? this.resolvedCustomLimits().postLimit : POST_LIMITS[this.plan]
    return currentCount < limit
  }

  isFreePlan(): boolean {
    return this.plan === 'free'
  }

  canViewAnalytics(): boolean {
    return tenantCanViewAnalytics(this)
  }

  canUseAiAssistant(): boolean {
    return tenantCanUseAiAssistant(this)
  }
}
